import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Drawer
} from '@mui/material';
import { ViewKanban, Add, Delete, ArrowBack, Edit, Check, Star, Close, AddComment, FilterList } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client/react';
import { TASKS_LIST_QUERY, TYPE_OF_TASKS_QUERY, PEOPLE_QUERY, CREATE_OR_UPDATE_TASK, DELETE_TASK } from '../../graphql/tasks';
import { BOARD_QUERY, CREATE_OR_UPDATE_BOARD_COLUMN, DESTROY_BOARD_COLUMN, CREATE_OR_UPDATE_BOARD, DESTROY_BOARD, BOARDS_LIST_QUERY } from '../../graphql/boards';
import { PROJECT_TREE_QUERY, GET_COMPANIES } from '../../graphql/projects';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from '../common/LoadingOverlay';
import TaskViewModal from '../tasks/TaskViewModal';
import TaskFormModal from '../tasks/TaskFormModal';
import TaskProgressModal from '../tasks/TaskProgressModal';
import TasksFilters from '../tasks/TasksFilters';
import ArchiveBoardDialog from './ArchiveBoardDialog';

const TaskBoard = () => {
  const navigate = useNavigate();
  const { boardId } = useParams();

  const { data: boardData, loading: boardLoading, refetch: refetchBoard } = useQuery(BOARD_QUERY, {
    variables: { id: boardId },
    fetchPolicy: 'network-only',
    skip: !boardId
  });

  const [createOrUpdateTask] = useMutation(CREATE_OR_UPDATE_TASK);
  const [deleteTask] = useMutation(DELETE_TASK);
  const [createOrUpdateBoard] = useMutation(CREATE_OR_UPDATE_BOARD);
  const [destroyBoard] = useMutation(DESTROY_BOARD);
  const [createOrUpdateBoardColumn] = useMutation(CREATE_OR_UPDATE_BOARD_COLUMN);
  const [destroyBoardColumn] = useMutation(DESTROY_BOARD_COLUMN);

  const [editingBoardName, setEditingBoardName] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [editingBoardDates, setEditingBoardDates] = useState(false);
  const [boardStartDate, setBoardStartDate] = useState('');
  const [boardEndDate, setBoardEndDate] = useState('');
  const [deleteBoardDialogOpen, setDeleteBoardDialogOpen] = useState(false);
  const [targetBoardId, setTargetBoardId] = useState('');
  const [targetColumnId, setTargetColumnId] = useState('');
  const [selectedTargetBoard, setSelectedTargetBoard] = useState(null);
  const [newColumnDialogOpen, setNewColumnDialogOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnProgressType, setNewColumnProgressType] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumnReorder, setDragOverColumnReorder] = useState(null);
  const [taskColumns, setTaskColumns] = useState({});
  const [taskOrder, setTaskOrder] = useState({});
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [editColumnName, setEditColumnName] = useState('');
  const [editColumnProgressType, setEditColumnProgressType] = useState('');
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [dragOverTask, setDragOverTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedColumnId, setSelectedColumnId] = useState(null);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskViewModalOpen, setTaskViewModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskProgressModalOpen, setTaskProgressModalOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      name: params.get('name') || '',
      project: params.get('project') || '',
      element: params.get('element') || '',
      company: params.get('company') || '',
      types: params.get('types') || '',
      priority: params.get('priority') || '',
      complete: params.get('complete') || '',
      responsible: params.get('responsible') || '',
      dateFrom: params.get('dateFrom') || '',
      dateTo: params.get('dateTo') || ''
    };
  };
  
  const [filters, setFilters] = useState(getFiltersFromURL);

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    navigate(`/boards/${boardId}?${params.toString()}`, { replace: true });
  };

  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useQuery(TASKS_LIST_QUERY, {
    variables: { 
      page: 1, 
      size: 1000,
      sort_column: 'id',
      sort_direction: 'ASC',
      filters: {
        project_id: filters.project ? parseInt(filters.project) : null,
        element_id: filters.element ? parseInt(filters.element) : null,
        priority: filters.priority ? parseInt(filters.priority) : null,
        name: filters.name || null,
        task_types: filters.types ? [filters.types.toString()] : null,
        responsible_ids: filters.responsible ? [parseInt(filters.responsible)] : null,
        complete: filters.complete ? (
          filters.complete === 'New' ? 0 :
          filters.complete === 'Done' ? 100 :
          filters.complete === 'In progress' ? -1 : null
        ) : null,
        start_date_from: filters.dateFrom ? new Date(filters.dateFrom + 'T00:00:00.000Z').toISOString() : null,
        start_date_to: filters.dateTo ? new Date(filters.dateTo + 'T23:59:59.999Z').toISOString() : null
      }
    },
    fetchPolicy: 'network-only'
  });

  const { data: typeOfTasksData } = useQuery(TYPE_OF_TASKS_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data: peopleData } = useQuery(PEOPLE_QUERY, {
    variables: { page: 1, size: 10000 },
    fetchPolicy: 'cache-first'
  });

  const { data: companiesData } = useQuery(GET_COMPANIES, {
    fetchPolicy: 'cache-first'
  });

  const { data: allProjectsTreeData } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: 1 },
    fetchPolicy: 'cache-first'
  });

  const { data: boardsData } = useQuery(BOARDS_LIST_QUERY, {
    fetchPolicy: 'cache-first'
  });

  const { data: targetBoardData } = useQuery(BOARD_QUERY, {
    variables: { id: selectedTargetBoard },
    fetchPolicy: 'network-only',
    skip: !selectedTargetBoard
  });

  const handleSaveBoardName = async () => {
    if (boardName.trim()) {
      try {
        await createOrUpdateBoard({
          variables: {
            id: boardId,
            name: boardName
          }
        });
        setEditingBoardName(false);
        refetchBoard();
      } catch (error) {
        console.error('Error updating board:', error);
      }
    }
  };

  const handleSaveBoardDates = async () => {
    try {
      await createOrUpdateBoard({
        variables: {
          id: boardId,
          start_date: boardStartDate || null,
          end_date: boardEndDate || null
        }
      });
      setEditingBoardDates(false);
      refetchBoard();
    } catch (error) {
      console.error('Error updating board dates:', error);
    }
  };

  const handleDeleteBoard = async () => {
    try {
      await destroyBoard({ 
        variables: { 
          id: boardId,
          target_board_column_id: targetBoardId === 'backlog' ? null : parseInt(targetColumnId)
        } 
      });
      navigate('/boards');
    } catch (error) {
      console.error('Error archiving board:', error);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask({ variables: { id: taskToDelete.id.toString() } });
      setDeleteTaskDialogOpen(false);
      setTaskToDelete(null);
      refetchBoard();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleAddColumn = async () => {
    if (newColumnName.trim()) {
      const maxPosition = Math.max(...(boardData?.board?.board_columns?.map(c => c.position) || [0]));
      try {
        await createOrUpdateBoardColumn({
          variables: {
            board_id: parseInt(boardId),
            name: newColumnName,
            position: maxPosition + 1,
            progress_type: newColumnProgressType || null
          }
        });
        setNewColumnName('');
        setNewColumnProgressType('');
        setNewColumnDialogOpen(false);
        refetchBoard();
      } catch (error) {
        console.error('Error creating column:', error);
      }
    }
  };

  const handleDeleteColumn = async (columnId) => {
    const column = columns.find(c => c.id === columnId);
    if (column?.is_default) return;
    
    try {
      await destroyBoardColumn({ variables: { id: columnId } });
      refetchBoard();
    } catch (error) {
      console.error('Error deleting column:', error);
    }
  };

  const handleEditColumn = (column) => {
    setEditingColumnId(column.id);
    setEditColumnName(column.name);
    setEditColumnProgressType(column.progress_type || '');
  };

  const handleSaveColumnName = async () => {
    if (editColumnName.trim()) {
      try {
        await createOrUpdateBoardColumn({
          variables: {
            id: editingColumnId,
            name: editColumnName,
            progress_type: editColumnProgressType || null
          }
        });
        setEditingColumnId(null);
        setEditColumnName('');
        setEditColumnProgressType('');
        refetchBoard();
      } catch (error) {
        console.error('Error updating column:', error);
      }
    }
  };

  const handleDragStart = (e, task) => {
    e.stopPropagation();
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
    setDragOverTask(null);
  };

  const handleTaskDragOver = (e, taskId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTask(taskId);
  };

  const handleDrop = async (e, columnId, targetTaskId = null) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedTask) {
      try {
        await createOrUpdateTask({
          variables: {
            id: draggedTask.id,
            board_column_id: parseInt(columnId)
          }
        });
        await refetchTasks();
        await refetchBoard();
      } catch (error) {
        console.error('Error moving task:', error);
      }
      setDraggedTask(null);
      setDragOverColumn(null);
      setDragOverTask(null);
    }
  };

  const handleColumnDragStart = (e, column) => {
    if (e.target.closest('.task-card')) {
      e.preventDefault();
      return;
    }
    setDraggedColumn(column);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnReorder(columnId);
  };

  const handleColumnDrop = async (e, targetColumn) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedColumn && draggedColumn.id !== targetColumn.id) {
      const sourcePos = draggedColumn.position;
      const targetPos = targetColumn.position;
      
      try {
        await createOrUpdateBoardColumn({
          variables: {
            id: draggedColumn.id,
            position: targetPos
          }
        });
        await createOrUpdateBoardColumn({
          variables: {
            id: targetColumn.id,
            position: sourcePos
          }
        });
        refetchBoard();
      } catch (error) {
        console.error('Error reordering columns:', error);
      }
    }
    setDraggedColumn(null);
    setDragOverColumnReorder(null);
  };

  const getPriorityColor = (priority) => {
    const colors = ['error', 'warning', 'info', 'success', 'default'];
    return colors[priority] || 'info';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const allProjects = React.useMemo(() => {
    const projects = {};
    if (allProjectsTreeData?.project_tree) {
      try {
        const parsed = JSON.parse(allProjectsTreeData.project_tree);
        const flattenTree = (items) => {
          items.forEach(item => {
            projects[item.id] = item;
            if (item.children) flattenTree(item.children);
          });
        };
        const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
        flattenTree(itemsArray);
      } catch (error) {
        console.error('Error parsing projects tree:', error);
      }
    }
    return projects;
  }, [allProjectsTreeData]);

  React.useEffect(() => {
    const newFilters = getFiltersFromURL();
    setFilters(newFilters);
  }, [window.location.search]);

  React.useEffect(() => {
    if (boardData?.board?.archived) {
      navigate('/boards');
    }
  }, [boardData, navigate]);

  if (tasksLoading || boardLoading) return <LoadingOverlay />;

  const allTasks = tasksData?.tasks?.list?.filter(task => task !== null) || [];
  
  const tasks = filters.company 
    ? allTasks.filter(task => task.companies?.some(c => c.id.toString() === filters.company))
    : allTasks;
  const columns = boardData?.board?.board_columns || [];
  const currentBoardName = boardData?.board?.name || `Board ${boardId}`;
  const currentStartDate = boardData?.board?.start_date || '';
  const currentEndDate = boardData?.board?.end_date || '';

  console.log('Board data:', boardData);
  console.log('Columns:', columns);

  const getTasksForColumn = (columnId) => {
    return tasks.filter(task => task.board_column?.id === columnId);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs>
          <Link 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/boards')}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Boards
          </Link>
          <Typography color="text.primary">{currentBoardName}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/boards')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <ViewKanban sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          {editingBoardName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                autoFocus
              />
              <IconButton size="small" color="primary" onClick={handleSaveBoardName}>
                <Check />
              </IconButton>
              <IconButton size="small" onClick={() => setEditingBoardName(false)}>
                <Close />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1">
                {currentBoardName}
              </Typography>
              <IconButton 
                size="small" 
                color="primary"
                onClick={() => {
                  setBoardName(currentBoardName);
                  setEditingBoardName(true);
                }}
              >
                <Edit />
              </IconButton>
            </Box>
          )}
          {editingBoardDates ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <TextField
                type="date"
                size="small"
                value={boardStartDate}
                onChange={(e) => setBoardStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                type="date"
                size="small"
                value={boardEndDate}
                onChange={(e) => setBoardEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <IconButton size="small" color="primary" onClick={handleSaveBoardDates}>
                <Check fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setEditingBoardDates(false)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {currentStartDate ? formatDate(currentStartDate) : 'Start Date'} - {currentEndDate ? formatDate(currentEndDate) : 'End Date'}
              </Typography>
              <IconButton 
                size="small" 
                color="primary"
                onClick={() => {
                  setBoardStartDate(currentStartDate);
                  setBoardEndDate(currentEndDate);
                  setEditingBoardDates(true);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewColumnDialogOpen(true)}
          >
            Add Column
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Delete />}
            onClick={() => setDeleteBoardDialogOpen(true)}
          >
            Archive Board
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterDrawerOpen(true)}
          >
            Filters
          </Button>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto',
        pb: 2,
        minHeight: 'calc(100vh - 200px)'
      }}>
        {columns.map((column) => (
          <Paper
            key={column.id}
            draggable={!draggedTask}
            onDragStart={(e) => handleColumnDragStart(e, column)}
            onDragOver={(e) => handleColumnDragOver(e, column.id)}
            onDrop={(e) => handleColumnDrop(e, column)}
            sx={{
              minWidth: 300,
              maxWidth: 300,
              p: 2,
              backgroundColor: dragOverColumn === column.id ? '#e3f2fd' : '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              transition: 'background-color 0.2s',
              border: dragOverColumnReorder === column.id ? '3px solid #1976d2' : dragOverColumn === column.id ? '2px solid #1976d2' : '2px solid transparent',
              opacity: draggedColumn?.id === column.id ? 0.5 : 1,
              cursor: draggedTask ? 'default' : 'grab',
              '&:active': { cursor: draggedTask ? 'default' : 'grabbing' }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              {editingColumnId === column.id ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Name"
                      value={editColumnName}
                      onChange={(e) => setEditColumnName(e.target.value)}
                      fullWidth
                    />
                    <IconButton size="small" color="primary" onClick={handleSaveColumnName}>
                      <Check fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    select
                    size="small"
                    label="Progress Type"
                    value={editColumnProgressType}
                    onChange={(e) => setEditColumnProgressType(e.target.value)}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={column.is_default}
                  >
                    <option value="">None</option>
                    <option value="new">New (0%)</option>
                    <option value="done">Done (100%)</option>
                  </TextField>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {column.is_default && <Star sx={{ fontSize: 20, color: 'warning.main' }} />}
                    <Typography variant="h6" fontWeight="bold">
                      {column.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {column.id !== 'todo' && (
                      <>
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => {
                            setSelectedTask(null);
                            setSelectedColumnId(column.id);
                            setTaskModalOpen(true);
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditColumn(column)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        {!column.is_default && (
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteColumn(column.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              overflowY: 'auto',
              minHeight: 100,
              flex: 1
            }}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
            >
              {getTasksForColumn(column.id).map((task, index) => (
                <Box
                  key={task.id}
                  onDragOver={(e) => handleTaskDragOver(e, task.id)}
                  onDrop={(e) => handleDrop(e, column.id, task.id)}
                  sx={{ position: 'relative' }}
                >
                  {dragOverTask === task.id && draggedTask?.id !== task.id && (
                    <Box
                      sx={{
                        height: 4,
                        backgroundColor: 'primary.main',
                        borderRadius: 1,
                        mb: 1
                      }}
                    />
                  )}
                  <Card
                    className="task-card"
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    sx={{
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      '&:hover': { boxShadow: 3 },
                      opacity: draggedTask?.id === task.id ? 0.5 : 1,
                      position: 'relative'
                    }}
                    onClick={(e) => {
                      if (!e.target.closest('.task-actions')) {
                        setSelectedTask(task);
                        setTaskViewModalOpen(true);
                      }
                    }}
                  >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                        {task.responsible?.slice(0, 3).map((person, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: 'grey.300',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              color: 'grey.700'
                            }}
                            title={`${person.last_name} ${person.first_name}`}
                          >
                            {person.last_name?.[0]}{person.first_name?.[0]}
                          </Box>
                        ))}
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: task.responsible?.length > 0 ? 0.5 : 0 }}>
                          {task.name}
                        </Typography>
                      </Box>
                      <Box className="task-actions" sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTask(task);
                            setTaskProgressModalOpen(true);
                          }}
                        >
                          <AddComment sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTask(task);
                            setTaskModalOpen(true);
                          }}
                        >
                          <Edit sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTaskToDelete(task);
                            setDeleteTaskDialogOpen(true);
                          }}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {task.project?.name || '-'} / {task.element?.name || '-'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      {task.task_type?.slice(0, 2).map((type, index) => (
                        <Chip 
                          key={index} 
                          label={type.name} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      {task.companies?.map((company, index) => (
                        <Chip 
                          key={index} 
                          label={company.name} 
                          size="small" 
                          variant="outlined"
                        />
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Chip
                        label={`${task.complete}%`}
                        size="small"
                        color={task.complete === 100 ? 'success' : 'default'}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>

      <Dialog open={newColumnDialogOpen} onClose={() => setNewColumnDialogOpen(false)}>
        <DialogTitle>Add New Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Column Name"
            fullWidth
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Progress Type"
            fullWidth
            value={newColumnProgressType}
            onChange={(e) => setNewColumnProgressType(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          >
            <option value="">None</option>
            <option value="new">New (0%)</option>
            <option value="done">Done (100%)</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewColumnDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddColumn} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      <ArchiveBoardDialog
        open={deleteBoardDialogOpen}
        onClose={() => {
          setDeleteBoardDialogOpen(false);
          setTargetBoardId('');
          setTargetColumnId('');
        }}
        onArchive={handleDeleteBoard}
        currentBoardId={boardId}
        boards={boardsData?.boards}
        targetBoardId={targetBoardId}
        setTargetBoardId={setTargetBoardId}
        targetColumnId={targetColumnId}
        setTargetColumnId={setTargetColumnId}
        setSelectedTargetBoard={setSelectedTargetBoard}
        targetBoardData={targetBoardData}
      />

      <Dialog open={deleteTaskDialogOpen} onClose={() => setDeleteTaskDialogOpen(false)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{taskToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTask} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <TaskViewModal
        open={taskViewModalOpen}
        onClose={() => {
          setTaskViewModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
      />
      <TaskFormModal 
        open={taskModalOpen} 
        onClose={() => {
          setTaskModalOpen(false);
          setSelectedTask(null);
          setSelectedColumnId(null);
        }}
        taskId={selectedTask?.id || null}
        onSave={async () => {
          await refetchTasks();
          await refetchBoard();
        }}
      />
      <TaskProgressModal
        open={taskProgressModalOpen}
        onClose={() => {
          setTaskProgressModalOpen(false);
          setSelectedTask(null);
        }}
        taskId={selectedTask?.id}
        onSave={async () => {
          await refetchTasks();
          await refetchBoard();
        }}
      />

      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 400, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto',
            '& > div': { 
              p: 0, 
              boxShadow: 'none',
              '& > div:first-child': {
                flexDirection: 'column !important',
                '& > div': {
                  flex: '1 1 auto !important',
                  minWidth: '100% !important',
                  maxWidth: '100% !important'
                }
              }
            }
          }}>
            <TasksFilters
              filters={filters}
              setFilters={updateFilters}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default TaskBoard;
